<template>
  <div
    ref="previewRef"
    class="player w-full h-full flex items-center justify-center"
    :class="{
      'player--playing': isPlaying,
      'player--touch-controls-visible': touchControlsVisible,
    }"
    @pointerdown="onPlayerPointerDown"
    @click="handlePreviewClick"
  >
    <template v-if="isVideo">
      <video
        v-show="isLoaded"
        ref="playerRef"
        playsinline
        :poster="poster"
        class="size-full object-contain cursor-pointer"
        v-on="mediaEvents"
      />
      <template v-if="!isLoaded">
        <img
          v-if="poster && !posterErrored"
          :src="poster"
          alt=""
          class="size-full object-contain"
          @load="onPosterLoaded"
          @error="posterErrored = true"
        />
        <div v-else :class="thumbnailClass">
          <Thumbnail type="video" />
        </div>
      </template>
    </template>
    <template v-if="isAudio">
      <div :class="thumbnailClass">
        <Thumbnail type="audio" />
      </div>
      <audio ref="playerRef" v-on="mediaEvents" />
    </template>
    <PlayerControls
      v-if="isAudio || isVideo"
      :playing="isPlaying"
      :current-time="currentTime"
      :duration="duration"
      :volume="volume"
      :muted="muted"
      :buffered="buffered"
      :disabled="!isLoaded"
      :over-media="controlsOverMedia"
      @toggle-playback="handleTogglePlayback"
      @toggle-mute="toggleMute"
      @seek="seek"
      @volume="setVolume"
    />
    <span
      v-if="playbackError"
      role="status"
      class="absolute bottom-16 inset-x-4 rounded-lg bg-black/60 px-3 py-2 text-center text-[13px] text-white"
    >
      {{ playbackError }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { FileType } from '@/shared/graphql/legacy/generated'
import { Thumbnail } from '@dacr/library'
import { getTokens } from '@dacr/auth'
import PlayerControls from './PlayerControls.vue'

const props = withDefaults(
  defineProps<{
    type: FileType
    url?: string | null
    poster?: string
    mimeType?: string
    thumbnailClass?: string
  }>(),
  {
    thumbnailClass: 'size-[170px] tablet-v:size-[280px] tablet-h:size-[320px]',
  }
)

const previewRef = ref<HTMLElement | null>(null)
const controlsOverMedia = ref(false)
let posterAspectRatio = 0

const playerRef = ref<HTMLVideoElement | HTMLAudioElement | null>(null)
const isLoaded = ref(false)
const isPlaying = ref(false)
const posterErrored = ref(false)
const currentTime = ref(0)
const duration = ref(0)
const volume = ref(1)
const muted = ref(false)
const buffered = ref<{ start: number; end: number }[]>([])
const playbackError = ref('')
const touchControlsVisible = ref(false)
let touchPointerId: number | null = null
let controlsHideTimer: ReturnType<typeof setTimeout> | undefined

const clearControlsHideTimer = () => {
  clearTimeout(controlsHideTimer)
  controlsHideTimer = undefined
}

const scheduleControlsHide = () => {
  clearControlsHideTimer()
  if (!isPlaying.value || touchPointerId !== null) return
  controlsHideTimer = setTimeout(() => {
    touchControlsVisible.value = false
  }, 2000)
}

const showTouchControls = () => {
  touchControlsVisible.value = true
  scheduleControlsHide()
}

const hideTouchControls = () => {
  clearControlsHideTimer()
  touchPointerId = null
  touchControlsVisible.value = false
}

const onPlayerPointerDown = (event: PointerEvent) => {
  if (event.pointerType === 'mouse') return
  touchPointerId = event.pointerId
  showTouchControls()
}

const onPlayerPointerEnd = (event: PointerEvent) => {
  if (event.pointerId !== touchPointerId) return
  touchPointerId = null
  scheduleControlsHide()
}

const onOutsidePointerDown = (event: PointerEvent) => {
  if (event.target instanceof Node && !previewRef.value?.contains(event.target))
    hideTouchControls()
}

watch(isPlaying, (playing) => {
  if (playing) showTouchControls()
  else hideTouchControls()
})

onMounted(() => {
  window.addEventListener('pointerdown', onOutsidePointerDown)
  window.addEventListener('pointerup', onPlayerPointerEnd)
  window.addEventListener('pointercancel', onPlayerPointerEnd)
  window.addEventListener('blur', hideTouchControls)
})

onUnmounted(() => {
  hideTouchControls()
  window.removeEventListener('pointerdown', onOutsidePointerDown)
  window.removeEventListener('pointerup', onPlayerPointerEnd)
  window.removeEventListener('pointercancel', onPlayerPointerEnd)
  window.removeEventListener('blur', hideTouchControls)
})

watch(
  () => props.poster,
  () => {
    posterErrored.value = false
    posterAspectRatio = 0
  }
)

const isAudio = computed(() => props.type === FileType.Audio)
const isVideo = computed(() => props.type === FileType.Video)

const updateControlsSurface = () => {
  const preview = previewRef.value
  const media = playerRef.value
  const aspectRatio =
    media instanceof HTMLVideoElement && media.videoHeight
      ? media.videoWidth / media.videoHeight
      : posterAspectRatio
  if (!preview || !aspectRatio || !isVideo.value) {
    controlsOverMedia.value = false
    return
  }
  const style = getComputedStyle(preview)
  const inset =
    parseFloat(style.getPropertyValue('--player-controls-inset')) || 8
  const top = parseFloat(style.paddingTop)
  const width =
    preview.clientWidth -
    parseFloat(style.paddingLeft) -
    parseFloat(style.paddingRight)
  const height = preview.clientHeight - top - parseFloat(style.paddingBottom)
  const mediaBottom = top + (height + Math.min(height, width / aspectRatio)) / 2
  controlsOverMedia.value = mediaBottom > preview.clientHeight - inset - 44
}

const onPosterLoaded = (event: Event) => {
  if (!(event.target instanceof HTMLImageElement)) return
  posterAspectRatio = event.target.naturalWidth / event.target.naturalHeight
  updateControlsSurface()
}

watch(
  previewRef,
  (preview, _, onCleanup) => {
    if (!preview) return
    const observer = new ResizeObserver(updateControlsSurface)
    observer.observe(preview)
    onCleanup(() => observer.disconnect())
  },
  { flush: 'post' }
)

const syncMedia = () => {
  const media = playerRef.value
  if (!media) return
  isPlaying.value = !media.paused && !media.ended
  currentTime.value = Number.isFinite(media.currentTime) ? media.currentTime : 0
  duration.value = Number.isFinite(media.duration) ? media.duration : 0
  volume.value = media.volume
  muted.value = media.muted
  buffered.value = duration.value
    ? Array.from({ length: media.buffered.length }, (_, index) => ({
        start: Math.max(
          0,
          (media.buffered.start(index) / duration.value) * 100
        ),
        end: Math.min(100, (media.buffered.end(index) / duration.value) * 100),
      }))
    : []
}

const mediaEvents = {
  play: syncMedia,
  pause: syncMedia,
  ended: syncMedia,
  timeupdate: syncMedia,
  durationchange: syncMedia,
  loadedmetadata: () => {
    syncMedia()
    updateControlsSurface()
  },
  progress: syncMedia,
  volumechange: syncMedia,
  emptied: syncMedia,
}

const handleTogglePlayback = async () => {
  const media = playerRef.value
  if (!media || !isLoaded.value) return
  playbackError.value = ''
  if (!media.paused) {
    media.pause()
    return
  }
  try {
    await media.play()
  } catch (error) {
    if (
      playerRef.value === media &&
      !(error instanceof DOMException && error.name === 'AbortError')
    ) {
      playbackError.value = 'Playback could not start. Try again.'
    }
  }
}

const handlePreviewClick = (event: MouseEvent) => {
  const isTouch =
    (event instanceof PointerEvent && event.pointerType !== 'mouse') ||
    window.matchMedia('(hover: none), (pointer: coarse)').matches
  if (isTouch && isPlaying.value) {
    showTouchControls()
    return
  }
  if (isVideo.value) handleTogglePlayback()
}

const seek = (time: number) => {
  if (!playerRef.value || !duration.value) return
  playerRef.value.currentTime = Math.max(0, Math.min(duration.value, time))
  syncMedia()
}

const toggleMute = () => {
  const media = playerRef.value
  if (!media) return
  if (media.muted || media.volume === 0) {
    if (media.volume === 0) media.volume = 1
    media.muted = false
  } else {
    media.muted = true
  }
  syncMedia()
}

const setVolume = (value: number) => {
  const media = playerRef.value
  if (!media) return
  media.volume = Math.max(0, Math.min(1, value))
  media.muted = value === 0
  syncMedia()
}

let teardown: Promise<unknown> = Promise.resolve()

watch(
  [playerRef, () => props.url],
  async ([media, url], _, onCleanup) => {
    let cancelled = false
    let player: { destroy: () => Promise<unknown> } | null = null
    onCleanup(() => {
      cancelled = true
      media?.pause()
      if (player) teardown = player.destroy().catch(() => undefined)
    })

    isLoaded.value = false
    isPlaying.value = false
    currentTime.value = 0
    duration.value = 0
    buffered.value = []
    playbackError.value = ''
    if (!media || !url) return

    try {
      await teardown
      if (cancelled) return
      const shaka = await import('shaka-player')
      if (cancelled) return
      shaka.default.polyfill.installAll()
      if (!shaka.default.Player.isBrowserSupported()) {
        playbackError.value = 'Playback is not supported in this browser.'
        return
      }

      const instance = new shaka.default.Player()
      player = instance
      await instance.attach(media)
      if (cancelled) return
      instance
        .getNetworkingEngine()
        ?.registerRequestFilter((_: unknown, request: { uris: string[] }) => {
          request.uris = request.uris.map((uri) => {
            const requestUrl = new URL(uri)
            requestUrl.searchParams.set(
              'Authorization',
              `Bearer ${getTokens().accessToken}`
            )
            return requestUrl.toString()
          })
        })
      await instance.load(url, null, props.mimeType)
      if (cancelled) return
      isLoaded.value = true
      syncMedia()
    } catch {
      if (cancelled) return
      isLoaded.value = false
      playbackError.value = 'This preview could not be loaded.'
    }
  },
  { flush: 'post' }
)
</script>

<style scoped>
.player {
  position: relative;
  container: player / inline-size;
}

.player :deep(.player-controls) {
  transform: translateY(0) scale(1);
  transform-origin: center bottom;
  filter: blur(0);
  transition:
    opacity 50ms cubic-bezier(0, 0, 0.2, 1),
    transform 50ms cubic-bezier(0, 0, 0.2, 1),
    filter 50ms cubic-bezier(0, 0, 0.2, 1);
}

.player--playing :deep(.player-controls) {
  opacity: 0;
  transform: translateY(4px) scale(0.95);
  filter: blur(8px);
  transition-duration: 300ms;
  pointer-events: none;
}

.player--playing:has(:focus-visible) :deep(.player-controls),
.player--playing:has(.player-range:active) :deep(.player-controls),
.player--playing:has(.player-seek--dragging) :deep(.player-controls) {
  opacity: 1;
  transform: translateY(0) scale(1);
  filter: blur(0);
  transition-duration: 50ms;
  pointer-events: auto;
}

@media (hover: hover) and (pointer: fine) {
  .player--playing:hover :deep(.player-controls) {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
    transition-duration: 50ms;
    pointer-events: auto;
  }
}

@media (hover: none), (pointer: coarse) {
  .player--touch-controls-visible :deep(.player-controls) {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
    transition-duration: 50ms;
    pointer-events: auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .player :deep(.player-controls) {
    transition: none;
  }
}
</style>
